import { parseDate, createCalendar } from '@internationalized/date';
import { useCalendar } from '@react-aria/calendar';
import { useCalendarState } from '@react-stately/calendar';
import type { DateValue, CalendarState } from '@react-stately/calendar';
import * as React from 'react';
import { I18nProvider, useLocale } from 'react-aria';
import { CalendarGrid } from './calendar-grid';
import { CalendarHeader } from './calendar-header';
import { cn } from '@utils';

export type ISingleDatePickerProps = {
	value?: string | null;
	defaultValue?: string | null;
	onChange?: (value: string) => void;
	notBefore?: string;
	notAfter?: string;
	isDisabled?: boolean;
	isReadOnly?: boolean;
	isDateUnavailable?: (date: string) => boolean;
	locale?: string;
	autoFocus?: boolean;
	className?: string;
	'data-testid'?: string;
};

const parseControlled = (raw: string | null | undefined): DateValue | null | undefined => {
	if (raw === undefined) {
		return undefined;
	}

	if (raw === null) {
		return null;
	}

	return parseDate(raw);
};

const parseOptional = (raw: string | undefined): DateValue | undefined => {
	return raw ? parseDate(raw) : undefined;
};

type InnerProps = Omit<ISingleDatePickerProps, 'locale'>;

const SingleDatePickerInner: React.VoidFunctionComponent<InnerProps> = ({
	value,
	defaultValue,
	onChange,
	notBefore,
	notAfter,
	isDisabled,
	isReadOnly,
	isDateUnavailable,
	autoFocus,
	className,
	'data-testid': dataTestId = 'single-date-picker',
}) => {
	const { locale } = useLocale();

	const controlledValue: DateValue | undefined | null = React.useMemo(() => parseControlled(value), [value]);

	const uncontrolledDefault: DateValue | undefined = React.useMemo(
		() => parseOptional(defaultValue ?? undefined),
		[defaultValue]
	);

	const minValue: DateValue | undefined = React.useMemo(() => parseOptional(notBefore), [notBefore]);
	const maxValue: DateValue | undefined = React.useMemo(() => parseOptional(notAfter), [notAfter]);

	const wrappedIsDateUnavailable = React.useMemo(() => {
		if (!isDateUnavailable) {
			return undefined;
		}

		return (date: DateValue) => isDateUnavailable(date.toString());
	}, [isDateUnavailable]);

	const handleChange = React.useCallback(
		(date: DateValue) => {
			onChange?.(date.toString());
		},
		[onChange]
	);

	const sharedProps = {
		value: controlledValue,
		defaultValue: uncontrolledDefault,
		onChange: handleChange,
		minValue,
		maxValue,
		isDisabled,
		isReadOnly,
		isDateUnavailable: wrappedIsDateUnavailable,
		autoFocus,
	};

	const state: CalendarState = useCalendarState({
		...sharedProps,
		locale,
		createCalendar,
	});

	const { calendarProps: rootProps, prevButtonProps, nextButtonProps, title } = useCalendar(sharedProps, state);

	return (
		<div {...rootProps} data-testid={dataTestId} className={cn('inline-flex flex-col p-4', className)}>
			<CalendarHeader title={title} prevButtonProps={prevButtonProps} nextButtonProps={nextButtonProps} />
			<CalendarGrid state={state} />
		</div>
	);
};

export const SingleDatePicker: React.VoidFunctionComponent<ISingleDatePickerProps> = ({ locale, ...rest }) => {
	if (locale) {
		return (
			<I18nProvider locale={locale}>
				<SingleDatePickerInner {...rest} />
			</I18nProvider>
		);
	}

	return <SingleDatePickerInner {...rest} />;
};
